import type { ReactNode } from "react";
import { Pressable, StyleSheet, Text, View } from "react-native";

import {
  fonts,
  radii,
  spacing,
  useThemedStyles,
  type ThemeColors,
} from "~/styles/theme";

export function SegmentedControl<TValue extends string | number>({
  accessibilityLabel,
  options,
  value,
  onChange,
  disabled = false,
}: {
  accessibilityLabel?: string;
  options: { label: ReactNode; value: TValue }[];
  value: TValue;
  onChange: (value: TValue) => void;
  disabled?: boolean;
}) {
  const stylesheet = useThemedStyles(makeStyles);
  return (
    <View
      accessibilityLabel={accessibilityLabel}
      accessibilityRole="tablist"
      style={stylesheet.control}
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <Pressable
            key={String(option.value)}
            accessibilityRole="tab"
            accessibilityState={{ selected: active, disabled }}
            disabled={disabled}
            onPress={() => onChange(option.value)}
            style={({ hovered, pressed }) => [
              stylesheet.button,
              active ? stylesheet.buttonActive : null,
              !active && (hovered || pressed) ? stylesheet.buttonHover : null,
              disabled ? stylesheet.buttonDisabled : null,
            ]}
          >
            <Text
              style={[stylesheet.text, active ? stylesheet.textActive : null]}
            >
              {option.label}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}

const makeStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    control: {
      minHeight: 38,
      flexDirection: "row",
      alignSelf: "flex-start",
      overflow: "hidden",
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radii.control,
      backgroundColor: colors.elevated,
    },
    button: {
      minHeight: 36,
      justifyContent: "center",
      paddingHorizontal: spacing.m,
      paddingVertical: spacing.s,
      borderRightWidth: 1,
      borderRightColor: colors.border,
    },
    buttonActive: {
      borderRightColor: colors.primary,
      backgroundColor: colors.primary,
    },
    buttonHover: { backgroundColor: colors.surface },
    buttonDisabled: { opacity: 0.5 },
    text: {
      color: colors.heading,
      fontFamily: fonts.medium,
      fontSize: 12,
      textTransform: "capitalize",
    },
    textActive: { color: "#FFFFFF" },
  });
